namespace $.$$ {

	export function $shm_hitalama_map_widget_float_table_grid_window( config: {
		heights: readonly number[]
		scroll_top: number
		viewport_height: number
		header_height: number
		overscan: number
		stick_end?: boolean
	} ) {
		const { heights } = config
		if( !heights.length ) return { window: [ 0, 0 ] as [ number, number ], prefix: [ 0 ] }

		const prefix = new Array< number >( heights.length + 1 )
		prefix[ 0 ] = 0
		for( let index = 0; index < heights.length; ++ index ) {
			prefix[ index + 1 ] = prefix[ index ] + Math.max( 1, heights[ index ] || 0 )
		}

		const body_top = Math.max( 0, config.scroll_top - config.header_height )
		const body_bottom = Math.max( body_top + 1, config.scroll_top + config.viewport_height - config.header_height )
		const upper_bound = ( value: number )=> {
			let left = 0
			let right = prefix.length
			while( left < right ) {
				const middle = ( left + right ) >> 1
				if( prefix[ middle ] <= value ) left = middle + 1
				else right = middle
			}
			return left
		}

		const over = Math.max( 0, Math.floor( config.overscan ) )
		let start = Math.max( 0, Math.min( heights.length - 1, upper_bound( body_top ) - 1 ) - over )
		let end = Math.min( heights.length, Math.max( start + 1, upper_bound( body_bottom - 1 ) ) + over )
		if( config.stick_end ) {
			end = heights.length
			start = end
			let visible_height = 0
			while( start > 0 && visible_height < Math.max( 1, config.viewport_height ) ) {
				start -= 1
				visible_height += prefix[ start + 1 ] - prefix[ start ]
			}
			start = Math.max( 0, start - over )
		}
		return { window: [ start, end ] as [ number, number ], prefix }
	}

	export class $shm_hitalama_map_widget_float_table_grid_list extends $.$shm_hitalama_map_widget_float_table_grid_list {
		private measured_heights = new Map< $mol_view, number >()
		private measured_rows = new Map< Element, $mol_view >()
		private observed_rows = new Set< Element >()
		private end_anchor = false
		private end_anchor_scroll = 0

		@ $mol_mem
		height_revision( next = 0 ) {
			return next
		}

		@ $mol_mem
		row_observer() {
			const observer = new this.$.$mol_dom_context.ResizeObserver( entries => this.rows_measure( entries ) )
			observer.observe( this.dom_node() )
			return Object.assign( observer, { destructor: ()=> observer.disconnect() } )
		}

		@ $mol_action
		rows_measure( entries: readonly ResizeObserverEntry[] ) {
			const root = this.dom_node()
			if( this.end_anchor && entries.some( entry => entry.target === root ) ) {
				const scroll = root.closest( '[mol_scroll]' ) as HTMLElement | null
				if( scroll ) {
					const end = Math.max( 0, scroll.scrollHeight - scroll.clientHeight )
					this.end_anchor_scroll = end
					if( Math.abs( scroll.scrollTop - end ) > 1 ) this.scroll_top( end )
				}
			}
			const window = this.view_window()
			const was_at_end = window[ 1 ] === this.sub().length
			let changed = false
			for( const entry of entries ) {
				const row = this.measured_rows.get( entry.target )
				if( !row ) continue
				const height = ( entry.target as HTMLElement ).offsetHeight
				if( !height || this.measured_heights.get( row ) === height ) continue
				this.measured_heights.set( row, height )
				changed = true
			}
			if( changed && was_at_end ) {
				this.end_anchor = true
				this.end_anchor_scroll = this.scroll_top()
			}
			if( changed ) this.height_revision( this.height_revision() + 1 )
		}

		rows_observe( rows: readonly $mol_view[] ) {
			const observer = this.row_observer()
			const next = new Set< Element >()
			for( const row of rows ) {
				const node = row.dom_node()
				next.add( node )
				this.measured_rows.set( node, row )
				if( !this.observed_rows.has( node ) ) observer.observe( node )
			}
			for( const node of this.observed_rows ) {
				if( next.has( node ) ) continue
				observer.unobserve( node )
				this.measured_rows.delete( node )
			}
			this.observed_rows = next
		}

		override item_height_min( index: number ) {
			this.height_revision()
			const row = this.sub()[ index ]
			return row ? this.measured_heights.get( row ) ?? this.height_estimate() : 0
		}

		@ $mol_mem
		height_estimate() {
			this.height_revision()
			const rows = new Set( this.sub() )
			let total = 0
			let count = 0
			for( const [ row, height ] of this.measured_heights ) {
				if( !rows.has( row ) ) {
					this.measured_heights.delete( row )
					continue
				}
				total += height
				count += 1
			}
			return Math.max( 1, count ? total / count : this.row_height() )
		}

		@ $mol_mem
		height_layout() {
			this.height_revision()
			const scroll_top = this.scroll_top()
			if( this.end_anchor && scroll_top + 1 < this.end_anchor_scroll ) this.end_anchor = false
			if( this.end_anchor ) this.end_anchor_scroll = scroll_top
			return $shm_hitalama_map_widget_float_table_grid_window({
				heights: this.sub().map( ( _, index )=> this.item_height_min( index ) ),
				scroll_top,
				viewport_height: Math.max( 1, this.viewport_height() ),
				header_height: Math.max( 0, this.header_height() ),
				overscan: this.overscan(),
				stick_end: this.end_anchor,
			})
		}

		override view_window( next?: [ number, number ] ): [ number, number ] {
			if( next ) return next
			if( !this.render_visible_only() ) return [ 0, this.sub().length ]
			return this.height_layout().window
		}

		override gap_before() {
			const layout = this.height_layout()
			return layout.prefix[ layout.window[ 0 ] ]
		}

		override gap_after() {
			const layout = this.height_layout()
			return layout.prefix.at( -1 )! - layout.prefix[ layout.window[ 1 ] ]
		}

		@ $mol_mem
		override sub_visible() {
			const rows = this.sub()
			if( !this.render_visible_only() ) return rows
			const [ start, end ] = this.view_window()
			const visible = rows.slice( start, end )
			this.rows_observe( visible )
			const before = this.gap_before()
			const after = this.gap_after()
			return [
				... before ? [ this.Gap_before() ] : [],
				... visible,
				... after ? [ this.Gap_after() ] : [],
			]
		}

	}

}
